import { useEffect, useMemo } from 'react';

import { useQuery } from 'react-query';

import { useAppDispatch } from 'state/hooks';
import { setQuotes } from 'state/slices/quotes';
import { getQuoteRequestsData } from 'app/api/quotes/service';
import { QuoteRequestStatus, QuoteRequestsListData } from 'app/api/quotes/types';
import { SortTypes } from '../types';
import { sortQuotes } from '../utils';

interface QuoteRequestsQueryI {
  enabled: boolean;
  sortValue: SortTypes;
  requestSample?: boolean;
  buyerCompanyId?: number;
  sellerCompanyId?: number;
  status: QuoteRequestStatus[];
  key?: string;
}

interface QuoteRequestsQueryReturn {
  isLoading: boolean;
  isSuccess: boolean;
  data: QuoteRequestsListData;
}

export const useQuoteRequestsQuery = ({
  enabled,
  sortValue,
  status,
  requestSample,
  buyerCompanyId,
  sellerCompanyId,
  key = 'quote-requests',
}: QuoteRequestsQueryI): QuoteRequestsQueryReturn => {
  const { isLoading, isFetching, isSuccess, data } = useQuery(
    [key, status, requestSample, buyerCompanyId, sellerCompanyId],
    () => getQuoteRequestsData({ status, requestSample, buyerCompanyId, sellerCompanyId }),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled,
    },
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (isSuccess && data) dispatch(setQuotes(data));
  }, [dispatch, data, isSuccess]);

  const sortedData = useMemo(() => (data ? sortQuotes([...data], sortValue) : []), [data, sortValue]);

  return {
    isLoading: isLoading || isFetching,
    isSuccess,
    data: sortedData,
  };
};
